// ============================================================
// AMOE CLAIM CARD - Free PSS Coins Entry Component
// ============================================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Spinner,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { Gift, Clock, Info, CheckCircle, AlertTriangle } from 'lucide-react';
import PokerButton from '../ui/PokerButton';
import PokerCard from '../ui/PokerCard';
import { POKER_COLORS } from '../../theme/designSystem';
import complianceService from '../../services/complianceService';
import { AMOEEligibility } from '../../types/complianceTypes';

interface AMOEClaimCardProps {
  userId: string;
  pkCashBalance: number;
  onClaimed?: (amount: number) => void;
  compact?: boolean;
}

const AMOEClaimCard: React.FC<AMOEClaimCardProps> = ({
  userId,
  pkCashBalance,
  onClaimed,
  compact = false,
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [eligibility, setEligibility] = useState<AMOEEligibility | null>(null);
  const [justClaimed, setJustClaimed] = useState(false);

  useEffect(() => {
    checkEligibility();
  }, [userId, pkCashBalance]);

  const checkEligibility = async () => {
    setLoading(true);
    try {
      const result = await complianceService.checkAMOEEligibility(userId);
      setEligibility(result);
    } catch (error) {
      console.error('Error checking AMOE eligibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const result = await complianceService.claimFreePKCash(userId);
      
      if (result.success) {
        setJustClaimed(true);
        toast({
          title: 'Free PSS Coins Claimed! ',

          description: `$${result.amount?.toFixed(2)} has been added to your wallet`,
          status: 'success',
          duration: 5000,
        });
        onClaimed?.(result.amount!);
        
        // Refresh eligibility
        await checkEligibility();
        
        // Reset just claimed after animation
        setTimeout(() => setJustClaimed(false), 3000);
      } else {
        toast({
          title: 'Unable to Claim',
          description: result.error,
          status: 'error',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Error claiming AMOE:', error);
      toast({
        title: 'Error',
        description: 'Failed to claim free PSS Coins. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setClaiming(false);
    }
  };

  const formatTimeRemaining = (nextEligibleAt: Date): string => {
    const now = new Date();
    const diff = nextEligibleAt.getTime() - now.getTime();
    
    if (diff <= 0) return 'Available now!';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  const getIneligibleIcon = () => {
    if (!eligibility?.reason) return Info;
    
    switch (eligibility.reason) {
      case 'COOLDOWN_ACTIVE':
        return Clock;
      case 'HAS_BALANCE':
        return Info;
      default:
        return AlertTriangle;
    }
  };

  if (loading) {
    return compact ? null : (
      <PokerCard p={4}>
        <HStack justify="center" py={2}>
          <Spinner size="sm" color={POKER_COLORS.cyan} />
          <Text color={POKER_COLORS.accentCyan} fontSize="sm">
            Checking eligibility...
          </Text>
        </HStack>
      </PokerCard>
    );
  }

  // Don't show if not eligible and compact mode
  if (compact && (!eligibility?.eligible || justClaimed)) {
    return null;
  }

  // Just claimed success state
  if (justClaimed) {
    return (
      <PokerCard 
        p={4} 
        bg="rgba(16, 185, 129, 0.15)"
        borderColor="#10b981"
        borderWidth="2px"
      >
        <HStack spacing={3}>
          <Box p={2} borderRadius="full" bg="rgba(16, 185, 129, 0.3)">
            <Icon as={CheckCircle} boxSize={6} color="#10b981" />
          </Box>
          <VStack align="start" spacing={0}>
            <Text color="#10b981" fontWeight="bold">
              PSS Coins Claimed! 

            </Text>
            <Text color={POKER_COLORS.accentCyan} fontSize="sm">
              $1.00 has been added to your wallet
            </Text>
          </VStack>
        </HStack>
      </PokerCard>
    );
  }

  // Eligible state - can claim
  if (eligibility?.eligible) {
    return (
      <PokerCard 
        p={compact ? 3 : 5}
        bg="linear-gradient(135deg, rgba(0, 217, 217, 0.15) 0%, rgba(212, 175, 55, 0.15) 100%)"
        borderColor={POKER_COLORS.gold}
        borderWidth="2px"
        position="relative"
        overflow="hidden"
      >
        {/* Shimmer effect */}
        <Box
          position="absolute"
          top={0}
          left={-100}
          w="100px"
          h="100%"
          bg="linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)"
          animation="shimmer 2s infinite"
          sx={{
            '@keyframes shimmer': {
              '0%': { left: '-100px' },
              '100%': { left: '100%' },
            },
          }}
        />

        <VStack spacing={compact ? 2 : 4} align="stretch">
          <HStack justify="space-between">
            <HStack spacing={2}>
              <Box 
                p={2} 
                borderRadius="full" 
                bg="rgba(212, 175, 55, 0.3)"
              >
                <Icon as={Gift} boxSize={compact ? 5 : 6} color={POKER_COLORS.gold} />
              </Box>
              <VStack align="start" spacing={0}>
                <Text 
                  color={POKER_COLORS.gold} 
                  fontWeight="bold" 
                  fontSize={compact ? 'md' : 'lg'}
                >
                  Free PSS Coins Available!
                </Text>
                {!compact && (
                  <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                    No purchase necessary
                  </Text>
                )}
              </VStack>
            </HStack>
            <Tooltip 
              label="Alternative Method of Entry (AMOE) - Get free PSS Coins without purchase"
              hasArrow
            >
              <Box cursor="help">
                <Icon as={Info} boxSize={4} color={POKER_COLORS.accentCyan} />
              </Box>
            </Tooltip>
          </HStack>

          {!compact && (
            <Box
              p={3}
              bg={POKER_COLORS.darkBase}
              borderRadius="lg"
              textAlign="center"
            >
              <Text color={POKER_COLORS.accentCyan} fontSize="sm" mb={1}>
                Claim your free entry:
              </Text>
              <Text color={POKER_COLORS.gold} fontSize="2xl" fontWeight="bold">
                $1.00 PSS Coins
              </Text>
              <Text color="gray.500" fontSize="xs">
                Once every 24 hours when balance is below $1
              </Text>
            </Box>
          )}

          <PokerButton
            variant="primary"
            size={compact ? 'sm' : 'md'}
            w="100%"
            onClick={handleClaim}
            isLoading={claiming}
            loadingText="Claiming..."
            bg={`linear-gradient(135deg, ${POKER_COLORS.gold} 0%, #b8860b 100%)`}
            _hover={{ transform: 'scale(1.02)' }}
          >
             Claim $1.00 Free

          </PokerButton>
        </VStack>
      </PokerCard>
    );
  }

  // Not eligible state
  return (
    <PokerCard p={compact ? 3 : 4} opacity={0.8}>
      <VStack spacing={3} align="stretch">
        <HStack spacing={3}>
          <Box p={2} borderRadius="full" bg={POKER_COLORS.darkBase}>
            <Icon as={getIneligibleIcon()} boxSize={5} color={POKER_COLORS.accentCyan} />
          </Box>
          <VStack align="start" spacing={0}>
            <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="sm">
              Free PSS Coins
            </Text>
            <Text color={POKER_COLORS.accentCyan} fontSize="xs">
              {eligibility?.reason === 'COOLDOWN_ACTIVE' && eligibility.nextEligibleAt
                ? formatTimeRemaining(eligibility.nextEligibleAt)
                : eligibility?.reason === 'HAS_BALANCE'
                ? `Available when PSS Coins is below $1 (current: $${eligibility.currentPKCash.toFixed(2)})`
                : complianceService.getAMOEIneligibleMessage(eligibility?.reason || 'ACCOUNT_SUSPENDED')
              }
            </Text>
          </VStack>
        </HStack>

        {!compact && eligibility?.reason === 'COOLDOWN_ACTIVE' && eligibility.nextEligibleAt && (
          <Box
            p={2}
            bg={POKER_COLORS.darkBase}
            borderRadius="md"
            textAlign="center"
          >
            <HStack justify="center" spacing={2}>
              <Icon as={Clock} boxSize={4} color={POKER_COLORS.accentCyan} />
              <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                Next claim: {eligibility.nextEligibleAt.toLocaleString()}
              </Text>
            </HStack>
          </Box>
        )}
      </VStack>
    </PokerCard>
  );
};

export default AMOEClaimCard;